import { useEffect, useState } from "react";
import { NavLink, Link, Outlet } from "react-router-dom";
import { Home, Users, BookOpen, HelpCircle, LogOut, ShieldCheck, Megaphone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-link">
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#FF6B00] font-display text-xl font-extrabold">TP</span>
      <span className="font-display text-xl font-extrabold uppercase tracking-tight leading-none">Team Play<span className="text-[#FF8A1F]"> Coach</span></span>
    </Link>
  );
}

const useLinks = () => {
  const { activeTeam, user } = useAuth();
  return [
    { to: "/", label: "Home", icon: Home, id: "dashboard", end: true },
    { to: "/teams", label: "Teams", icon: Users, id: "teams", end: true },
    { to: activeTeam ? `/teams/${activeTeam.id}/playbook` : "/teams", label: "Playbook", icon: BookOpen, id: "playbook" },
    { to: "/help", label: "Help", icon: HelpCircle, id: "help" },
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck, id: "admin" }] : []),
  ];
};

const cls = ({ isActive }) => `flex items-center gap-2 rounded-lg px-3 min-h-[44px] font-bold text-sm transition-colors ${isActive ? "bg-[#FF6B00] text-white" : "text-slate-300 hover:bg-[#1A2A4A] hover:text-white"}`;

export default function Layout() {
  const { signOut, activeTeam } = useAuth();
  const links = useLinks();
  const [notice, setNotice] = useState("");
  useEffect(() => { api.get("/app-settings").then((r) => setNotice(r.data.announcement)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-[#070D18] text-white pb-24 md:pb-10">
      <header className="sticky top-0 z-40 bg-[#0B1528] border-b border-[#1F3358]">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink key={l.id} to={l.to} end={l.end} className={cls} data-testid={`nav-${l.id}-link`}><l.icon size={18} />{l.label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {activeTeam && <span className="hidden lg:inline text-xs text-slate-400" data-testid="nav-active-team">{activeTeam.name}</span>}
            <button onClick={signOut} className="btn btn-ghost px-3" aria-label="Sign out" data-testid="nav-signout-btn"><LogOut size={18} /><span className="hidden sm:inline">Sign out</span></button>
          </div>
        </div>
      </header>
      {notice && <div className="bg-[#FF6B00]/15 border-b border-[#FF6B00]/40 text-sm text-white px-4 py-2 flex items-center justify-center gap-2" data-testid="app-announcement"><Megaphone size={16} className="text-[#FF8A1F] shrink-0" />{notice}</div>}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8"><Outlet /></main>
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-[#0B1528]/95 backdrop-blur-md border-t border-[#1F3358] px-2 py-1.5 flex justify-around" data-testid="mobile-bottom-nav">
        {links.map((l) => (
          <NavLink key={l.id} to={l.to} end={l.end} className={({ isActive }) => `flex flex-col items-center justify-center min-w-[56px] min-h-[52px] rounded-lg text-[11px] font-bold ${isActive ? "text-[#FF8A1F]" : "text-slate-300"}`} data-testid={`mnav-${l.id}-link`}>
            <l.icon size={22} />{l.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
